import * as React from 'react';
import { useIntl } from 'react-intl';
import Typography from '@/components/sushi/Typography';
import { styled } from '../../../stitches.config';
import Button from '@/components/sushi/Button';
import { SeoTextWithNavigation } from '@/components/Seo/SeoTextWithNavigation';
import * as $segment from '@/utils/segment';

export const SeoContent = () => {
  const { formatMessage } = useIntl();

  const handleCtaClick = () => {
    $segment.event('simulator_cta_clicked', { simulator: 'budget' });
  };

  return (
    <WhiteWrapper>
      <Content style={{ paddingTop: 72 }}>
        <CardWrapper>
          <Card>
            <iframe
              width="654"
              height="368"
              src={formatMessage({ id: 'budget_calculator.content_cards.youtube_link' })}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{ width: '100%', aspectRatio: '560 / 315' }}></iframe>
          </Card>

          <Card gradient>
            <Typography variant={'bodyL'} color={'gradient'} center>
              {formatMessage({ id: 'budget_calculator.content_cards.cta_headline' })}
            </Typography>
            <Typography variant={'subHeadingM'} color={'secondaryAlt'} center>
              {formatMessage({ id: 'budget_calculator.content_cards.cta_description' })}
            </Typography>
            <Button
              as="a"
              target={'_blank'}
              href={
                'https://app.finary.com/v2/signup?utm_source=simulators&utm_medium=tools&utm_campaign=simulators_tools_budgetcalculator&utm_content=cta'
              }
              variant={'tertiaryDark'}
              style={{ marginTop: 12 }}
              onClick={handleCtaClick}>
              {formatMessage({ id: 'budget_calculator.content_cards.cta_button' })}
            </Button>
          </Card>
        </CardWrapper>
      </Content>
      <Content>
        <SeoTextWithNavigation
          entries={[
            {
              id: 'how-it-works',
              title: formatMessage({ id: 'budget_calculator.seo_content.how_it_works.title' }),
              text: formatMessage({ id: 'budget_calculator.seo_content.how_it_works.markdown' }),
            },
            {
              id: 'fill-in-revenues',
              title: formatMessage({ id: 'budget_calculator.seo_content.fill_in_revenues.title' }),
              text: formatMessage({
                id: 'budget_calculator.seo_content.fill_in_revenues.markdown',
              }),
            },
            {
              id: 'fill-in-investments',
              title: formatMessage({
                id: 'budget_calculator.seo_content.fill_in_investments.title',
              }),
              text: formatMessage({
                id: 'budget_calculator.seo_content.fill_in_investments.markdown',
              }),
            },
            {
              id: 'fill-in-expenses',
              title: formatMessage({
                id: 'budget_calculator.seo_content.fill_in_expenses.title',
              }),
              text: formatMessage({
                id: 'budget_calculator.seo_content.fill_in_expenses.markdown',
              }),
            },
            {
              id: 'share-chart',
              title: formatMessage({
                id: 'budget_calculator.seo_content.share_chart.title',
              }),
              text: formatMessage({
                id: 'budget_calculator.seo_content.share_chart.markdown',
              }),
            },
            {
              id: 'howto-read-chart',
              title: formatMessage({
                id: 'budget_calculator.seo_content.howto_read_chart.title',
              }),
              text: formatMessage({
                id: 'budget_calculator.seo_content.howto_read_chart.markdown',
              }),
            },
            {
              id: 'chart-inflow-outflow',
              title: formatMessage({
                id: 'budget_calculator.seo_content.chart_inflow_outflow.title',
              }),
              text: formatMessage({
                id: 'budget_calculator.seo_content.chart_inflow_outflow.markdown',
              }),
            },
            {
              id: 'actual-vs-possible-savings-rate',
              title: formatMessage({
                id: 'budget_calculator.seo_content.actual_vs_possible_savings_rate.title',
              }),
              text: formatMessage({
                id: 'budget_calculator.seo_content.actual_vs_possible_savings_rate.markdown',
              }),
            },
            {
              id: 'what-to-do-next',
              title: formatMessage({
                id: 'budget_calculator.seo_content.what_to_do_next.title',
              }),
              text: formatMessage({
                id: 'budget_calculator.seo_content.what_to_do_next.markdown',
              }),
            },
          ]}
        />
      </Content>
    </WhiteWrapper>
  );
};

const WhiteWrapper = styled('div', {
  backgroundColor: '#fff',
  width: '100%',
  padding: '0 20',
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
});

const Content = styled('div', {
  width: '100%',
  maxWidth: 1264,
  margin: '0 auto',
  padding: '24px',
});

const CardWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 32,
  margin: '0 -16px',

  '@md': {
    flexDirection: 'column',
  },
});

const Card = styled('div', {
  boxSizing: 'border-box',
  borderRadius: '$4',
  overflow: 'hidden',
  position: 'relative',
  zIndex: 1,
  flexBasis: 'calc(50% - 32px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 368,
  maxWidth: 654,
  gap: 12,

  variants: {
    gradient: {
      true: {
        '--border-width': '1px',
        border: `solid var(--border-width) transparent`,
        padding: 16,
        '&::before': {
          content: '',
          backgroundImage: 'linear-gradient(139deg, #8DADFF 10%, #C89C69 59%)',
          position: 'absolute',
          zIndex: -1,
          inset: 0,
          borderRadius: 'inherit',
        },
        '&::after': {
          content: '',
          backgroundColor: '#fff',
          position: 'absolute',
          inset: 1,
          borderRadius: 'calc($4 - var(--border-width))',
          zIndex: -1,
        },
      },
    },
  },
});
